import { UserAssetInfo, UserAssetType } from "@/core/common/types";
import { AssetMetadataCollection, AssetMetadataGenerator } from "@/core/common/types/assetV2";
import { classNames } from "@/core/utils/classname-utils";
import { SecondaryButtonClassNameInactive } from "components/constants/class-names";
import { mergeRefs } from "components/utils/merge-refs";
import { Tooltip, TooltipProps } from "components/utils/tooltip";
import { editorContextStore } from "contexts/editor-context";
import React from "react";
import { useInView } from "react-intersection-observer";
import { PresetImageGridItem, PresetImageGridItemPropsAddItemHandler } from "./preset-image-grid";

export function useUserImageAssets() {
  const userImageAssetInfoCollection = editorContextStore(
    (state) => state.userImageAssetInfoCollection,
  );
  const backend = editorContextStore((state) => state.backend);
  const publicUserId = editorContextStore((state) => state.publicUserId);
  const assetMetadataGeneratorRef = editorContextStore((state) => state.assetMetadataGeneratorRef);
  const setAssetMetadataCollection = editorContextStore(
    (state) => state.setAssetMetadataCollection,
  );
  const assetMetadataCollection = editorContextStore((state) => state.assetMetadataCollection);
  React.useEffect(() => {
    if (!backend || !publicUserId) {
      return;
    }

    if (!assetMetadataGeneratorRef.current) {
      assetMetadataGeneratorRef.current = backend.getAssetMetadataGenerator({
        assetType: UserAssetType.UploadedProduct,
        batchSize: 12,
        publicUserId,
      }) as unknown as AssetMetadataGenerator;
      assetMetadataGeneratorRef.current?.getNextBatch().then((assetInfo) => {
        setAssetMetadataCollection(
          assetInfo.reduce<AssetMetadataCollection>((result, i) => {
            result[i.id] = i;
            return result;
          }, {}),
        );
      });
    }
  }, [backend, publicUserId, assetMetadataGeneratorRef, setAssetMetadataCollection]);

  const [lastRowRef, lastRowInView] = useInView();

  React.useEffect(() => {
    if (!lastRowInView || !assetMetadataGeneratorRef.current) {
      return;
    }

    assetMetadataGeneratorRef.current.getNextBatch().then((assetInfo) => {
      setAssetMetadataCollection((prevCollection) => ({
        ...prevCollection,
        ...assetInfo.reduce<AssetMetadataCollection>((result, i) => {
          result[i.id] = i;
          return result;
        }, {}),
      }));
    });
  }, [lastRowInView, assetMetadataGeneratorRef, setAssetMetadataCollection]);

  const userImageAssets = React.useMemo(
    () => Object.values(assetMetadataCollection),
    [assetMetadataCollection],
  );

  return {
    userImageAssets,
    lastRowRef,
  };
}

export const UserAssetLibraryItem = React.forwardRef(
  (
    {
      imageItem,
      isIdle = true,
      onAddItem,
      ...props
    }: TooltipProps & {
      imageItem: UserAssetInfo;
      isIdle?: boolean;
      onAddItem: PresetImageGridItemPropsAddItemHandler;
    },
    forwardedRef: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const editor = editorContextStore((state) => state.editor);

    const [elementRef, inView] = useInView();

    const storagePath = imageItem.storagePath;

    const [url, setUrl] = React.useState("");

    React.useEffect(() => {
      if (!editor) {
        return;
      }

      editor.assets
        .loadAsset({
          type: "image-storage",
          path: storagePath,
        })
        .then((dataUrl) => {
          if (!dataUrl) {
            return;
          }
          setUrl(dataUrl);
        });
    }, [editor, storagePath]);

    return (
      <Tooltip
        {...props}
        triggerChildren={
          <PresetImageGridItem
            // eslint-disable-next-line
            ref={mergeRefs([forwardedRef, elementRef])}
            url={inView ? url : ""}
            className={classNames(
              SecondaryButtonClassNameInactive,
              url ? "cursor-pointer" : "cursor-wait",
            )}
            onAddItem={(args) =>
              onAddItem({
                ...args,
                caption: imageItem.caption,
              })
            }
          />
        }
        contentClassName="pointer-events-none"
        contentChildren={
          <div>{url ? "Click to add this asset to the canvas." : "Loading asset ..."}</div>
        }
      />
    );
  },
);
